import { useRef, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router";
import { addAccount, addAccountDocument } from "./internalAccounts.js";
import { streamChat, uploadAttachment } from "../../utils/agentsApi";

const ACCEPT = ".xlsx,.xls,.csv,.pdf,.docx,.txt,.json";

const PROMPT = [
    "Build two documents from the attached files.",
    "Call emit_benefit_plan once for every plan sheet. Good Times has Partnership Plan, Traditional PPO, and Traditional EPO.",
    "emit_account_details is contacts, address, stop-loss, and utilization management.",
    "emit_account_proposal follows account-proposal.json and lists every plan.",
    "Put CPT and revenue codes on the matching inNetwork service.",
    "Omit fields the files do not state.",
].join(" ");

const parseCanonical = (result) => {
    if (!result) return null;
    if (typeof result === "object") return result;
    if (typeof result !== "string") return null;
    try {
        return JSON.parse(result);
    } catch {
        return null;
    }
};

const AddAccount = () => {
    const { desk } = useOutletContext();
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const filesRef = useRef([]);
    const [files, setFiles] = useState([]);
    const [dragOver, setDragOver] = useState(false);
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");
    const [running, setRunning] = useState(false);

    const startBlank = () => {
        const created = addAccount();
        navigate(`/internal/${desk.id}/${created.id}`, { state: { edit: true } });
    };

    const addFiles = (list) => {
        const next = [...list].filter(Boolean);
        if (!next.length) return;
        const seen = new Set(filesRef.current.map((file) => `${file.name}:${file.size}`));
        const unique = next.filter((file) => !seen.has(`${file.name}:${file.size}`));
        if (!unique.length) return;
        filesRef.current = [...filesRef.current, ...unique];
        setFiles(filesRef.current);
        setError("");
    };

    const build = async () => {
        if (!files.length || running) return;
        setRunning(true);
        setError("");
        setStatus("Uploading files");
        const controller = new AbortController();
        let account = null;
        let proposal = null;
        try {
            const attachments = [];
            for (const file of files) {
                setStatus(`Uploading ${file.name}`);
                attachments.push(await uploadAttachment(file));
            }
            setStatus("Reading the files");
            for await (const event of streamChat({
                agentId: "canonicalAccountAgent",
                message: PROMPT,
                attachments,
                signal: controller.signal,
            })) {
                if (event.type === "tool_call") {
                    if (event.name === "emit_benefit_plan") setStatus("Writing a benefit plan");
                    else if (event.name === "emit_account_details") setStatus("Writing account details");
                    else if (event.name === "emit_account_proposal") setStatus("Writing the proposal");
                    else setStatus(`Working · ${event.name}`);
                }
                if (event.type === "tool_result") {
                    const parsed = parseCanonical(event.result);
                    if (!parsed) continue;
                    if (event.name === "emit_benefit_plan") {
                        account = account || {};
                        const plans = [...(account.benefitPlans || [])];
                        const index = plans.findIndex((plan) => plan.planName === parsed.planName);
                        if (index >= 0) plans[index] = parsed;
                        else plans.push(parsed);
                        account = { ...account, benefitPlans: plans };
                        if (parsed.planName) setStatus(`Wrote ${parsed.planName}`);
                    }
                    if (event.name === "emit_account_details") {
                        account = { ...parsed, benefitPlans: account?.benefitPlans || [] };
                    }
                    if (event.name === "emit_account_proposal") proposal = parsed;
                }
                if (event.type === "error") throw new Error(event.message || "The agent stopped");
            }
            if (!account) throw new Error("The files did not produce an account");
            const created = addAccountDocument(proposal ? { ...account, proposal } : account);
            navigate(`/internal/${desk.id}/${created.id}`, { state: { edit: true } });
        } catch (err) {
            setError(err.message || "Could not build the account");
            setStatus("");
            setRunning(false);
        }
    };

    return (
        <>
            <Link className="internal-work__back" to={`/internal/${desk.id}`}>Accounts</Link>
            <p className="internal-work__eyebrow">{desk.label}</p>
            <h1>Add account</h1>
            <p className="internal-work__lead">Open account details empty, or fill that same form from the workbooks.</p>
            <div className="internal-work__entry">
                <section className="internal-work__pane">
                    <div>
                        <p className="internal-work__eyebrow">Blank form</p>
                        <h2>Account details, empty</h2>
                        <p>Name, contacts, addresses, stop-loss, and plans. Nothing is filled in.</p>
                    </div>
                    <ol className="internal-work__outline">
                        <li><span>Details</span><em>Name, contacts, addresses</em></li>
                        <li><span>Stop-loss</span><em>Carrier, specific, aggregate</em></li>
                        <li><span>Plans</span><em>Tiers, networks, services</em></li>
                    </ol>
                    <button type="button" className="btn edit-btn" onClick={startBlank}>Start from blank form</button>
                </section>
                <section className="internal-work__pane">
                    <div>
                        <p className="internal-work__eyebrow">Drop files</p>
                        <h2>Fill the form from files</h2>
                        <p>Proposal, benefit summary, MCW, and config grid.</p>
                    </div>
                    <div
                        className={`internal-work__drop${dragOver ? " is-over" : ""}`}
                        onDragOver={(event) => {
                            event.preventDefault();
                            setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(event) => {
                            event.preventDefault();
                            setDragOver(false);
                            addFiles(event.dataTransfer.files);
                        }}
                        onClick={() => inputRef.current?.click()}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") inputRef.current?.click();
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <strong>Drop proposal, benefit summary, MCW, and config grid</strong>
                        <span>xlsx, csv, pdf, docx, or json</span>
                        <input
                            ref={inputRef}
                            type="file"
                            accept={ACCEPT}
                            multiple
                            hidden
                            onChange={(event) => {
                                addFiles(event.target.files);
                                event.target.value = "";
                            }}
                        />
                    </div>
                    {files.length > 0 && (
                        <ul className="internal-work__files">
                            {files.map((file) => (
                                <li key={`${file.name}:${file.size}`}>
                                    <span>{file.name}</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            filesRef.current = filesRef.current.filter((row) => row !== file);
                                            setFiles(filesRef.current);
                                        }}
                                        disabled={running}
                                    >
                                        Remove
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="internal-work__form-actions">
                        <button type="button" className="btn edit-btn" onClick={build} disabled={!files.length || running}>
                            {running ? "Building" : "Fill the form"}
                        </button>
                        {status && <p>{status}</p>}
                    </div>
                    {error && <p className="internal-work__service-error">{error}</p>}
                </section>
            </div>
        </>
    );
};

export default AddAccount;
